import { CloseOutlined, FileOutlined } from '@ant-design/icons';
import { Button, Flex, Input, Select, Typography } from 'antd';
import type { EvidenceUploadFileListOption } from 'pages/EvidencePage/evidenceConfig';
import { useIntl } from 'react-intl';
import { TypedFormatMessageFn } from 'types/intl';

export interface IEvidenceUploadFileListProps {
  files: File[];
  onRemove: (index: number) => void;
  setDescription: (index: number, value: string) => void;
  options?: EvidenceUploadFileListOption[];
  allowDescription?: boolean;
  descriptionRequired?: boolean;
  descriptions: string[];
}

export function EvidenceUploadFileList({
  files,
  onRemove,
  setDescription,
  options,
  allowDescription = false,
  descriptionRequired = false,
  descriptions,
}: IEvidenceUploadFileListProps) {
  const { formatMessage }: { formatMessage: TypedFormatMessageFn } = useIntl();
  const hasDescriptionOptions = Boolean(options && options.length > 0);

  return (
    <Flex vertical className='mt-3 gap-2'>
      {files.map((file, index) => (
        <Flex key={index} className='gap-2 rounded-lg border border-gray-100 bg-gray-50 p-2'>
          <Flex align='center' justify='center' className='h-7 w-7 shrink-0 rounded bg-white'>
            <FileOutlined className='h-3 w-3 shrink-0 text-black' />
          </Flex>
          <Flex vertical className='min-w-0 flex-1 gap-2'>
            <Flex align='center' justify='space-between' className='min-w-0 gap-2'>
              <Typography.Text className='truncate text-xs leading-5 text-black'>{file.name}</Typography.Text>
              <Button
                type='text'
                className='flex h-6 min-h-6 w-6 min-w-6 shrink-0 items-center justify-center border-0 bg-transparent p-1.5 shadow-none'
                icon={<CloseOutlined className='text-xs leading-none' />}
                onClick={() => onRemove(index)}
              />
            </Flex>
            {allowDescription ? (
              hasDescriptionOptions ? (
                <Select
                  placeholder={formatMessage({
                    id: descriptionRequired
                      ? 'evidence.upload.fileList.documentTypeRequired'
                      : 'evidence.upload.fileList.selectDescription',
                  })}
                  value={descriptions[index] || undefined}
                  onChange={value => setDescription(index, value)}
                  options={options}
                  className='evidence-upload-select w-full'
                  size='small'
                  status={descriptionRequired && !descriptions[index]?.trim() ? 'error' : undefined}
                  aria-required={descriptionRequired}
                />
              ) : (
                <Input
                  placeholder={formatMessage({
                    id: descriptionRequired
                      ? 'evidence.upload.fileList.descriptionRequired'
                      : 'evidence.upload.fileList.descriptionOptional',
                  })}
                  value={descriptions[index] || ''}
                  onChange={e => setDescription(index, e.target.value)}
                  size='small'
                  maxLength={120}
                  status={descriptionRequired && !descriptions[index]?.trim() ? 'error' : undefined}
                  aria-required={descriptionRequired}
                />
              )
            ) : null}
          </Flex>
        </Flex>
      ))}
    </Flex>
  );
}
